import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Chart from 'react-apexcharts';
import { motion } from 'framer-motion';
import { FaSearch, FaLightbulb, FaCalculator, FaChartLine } from 'react-icons/fa';

const HalfLifeCalculator = () => {
  const [initialAmount, setInitialAmount] = useState('');
  const [currentAmount, setCurrentAmount] = useState('');
  const [timeElapsed, setTimeElapsed] = useState('');
  const [halfLife, setHalfLife] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [showGraph, setShowGraph] = useState(false);

  const calculateHalfLife = () => {
    setError('');
    
    // Validate inputs
    const N0 = parseFloat(initialAmount);
    const N = parseFloat(currentAmount);
    const t = parseFloat(timeElapsed);
    const T = parseFloat(halfLife);

    if (isNaN(N0) || N0 <= 0) {
      setError('Initial amount must be a positive number');
      return;
    }

    if (isNaN(N) || N <= 0) {
      setError('Current amount must be a positive number');
      return;
    }

    if (N > N0) {
      setError('Current amount cannot be greater than initial amount');
      return;
    }

    if (isNaN(t) || t < 0) {
      setError('Time elapsed must be a non-negative number');
      return;
    }

    if (isNaN(T) || T <= 0) {
      setError('Half-life must be a positive number');
      return;
    }

    // Calculate decay constant
    const lambda = Math.log(2) / T;

    // Calculate number of half-lives
    const nHalfLives = Math.log(N0 / N) / Math.log(2);

    // Calculate time for current amount
    const timeForCurrent = (Math.log(N0 / N) / lambda);

    // Calculate remaining amount at time t
    const remainingAtTime = N0 * Math.exp(-lambda * t);

    // Calculate percentage remaining
    const percentageRemaining = (remainingAtTime / N0) * 100;

    const result = {
      initialAmount: N0,
      currentAmount: N,
      timeElapsed: t,
      halfLife: T,
      decayConstant: lambda.toFixed(6),
      remainingAmount: remainingAtTime.toFixed(2),
      percentageRemaining: percentageRemaining.toFixed(2),
      timeForCurrent: timeForCurrent.toFixed(2),
      nHalfLives: nHalfLives.toFixed(2)
    };

    setResult(result);
    setShowGraph(true);
  };

  const generateGraphData = () => {
    if (!result) return null;

    // Generate data for decay curve
    const series = [
      {
        name: 'Amount Remaining',
        data: Array.from({ length: 10 }, (_, i) => {
          const time = i * result.halfLife;
          const remaining = result.initialAmount * Math.exp(-Math.log(2) * (time / result.halfLife));
          return { x: time, y: remaining };
        })
      }
    ];

    const options = {
      chart: {
        type: 'line',
        height: 400,
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: false
        }
      },
      stroke: {
        width: 2,
        curve: 'smooth'
      },
      xaxis: {
        title: {
          text: 'Time (seconds)',
        },
        labels: {
          formatter: function (value) {
            return value.toFixed(2);
          }
        }
      },
      yaxis: {
        title: {
          text: 'Amount Remaining',
        },
        labels: {
          formatter: function (value) {
            return value.toFixed(2);
          }
        }
      },
      title: {
        text: 'Radioactive Decay Curve',
        align: 'center',
      },
      colors: ['#4F46E5'],
      annotations: {
        yaxis: [{
          y: result.initialAmount / 2,
          borderColor: '#E5E7EB',
          label: {
            show: true,
            text: 'Half-Life',
            style: {
              color: '#4B5563',
              background: '#fff',
            },
          }
        }]
      }
    };

    return { series, options };
  };

  return (
    <div className="min-h-screen bg-gradient-to-r from-purple-100 to-pink-100">
      <Helmet>
        <title>Half-Life Calculator | Physics Tool</title>
        <meta name="description" content="Calculate radioactive decay using the half-life formula. Interactive calculator for nuclear physics and chemistry." />
        <meta name="keywords" content="half-life calculator, radioactive decay calculator, nuclear physics calculator, chemistry calculator, physics tool" />
        <meta property="og:title" content="Half-Life Calculator | Physics Tool" />
        <meta property="og:description" content="Calculate radioactive decay using the half-life formula. Interactive calculator for nuclear physics and chemistry." />
        <meta property="og:image" content="/social-preview.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Half-Life Calculator | Physics Tool" />
        <meta name="twitter:description" content="Calculate radioactive decay using the half-life formula. Interactive calculator for nuclear physics and chemistry." />
        <link rel="canonical" href="https://yourdomain.com/physics-calculators/half-life" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="header-icon text-4xl text-purple-600 mb-4">
            <FaSearch />
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Half-Life Calculator</h1>
          <p className="text-xl text-gray-600">Calculate Radioactive Decay</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold mb-4">Calculator</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Initial Amount (N₀)</label>
                <input
                  type="number"
                  value={initialAmount}
                  onChange={(e) => setInitialAmount(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter initial amount"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Current Amount (N)</label>
                <input
                  type="number"
                  value={currentAmount}
                  onChange={(e) => setCurrentAmount(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter current amount"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Time Elapsed (t)</label>
                <input
                  type="number"
                  value={timeElapsed}
                  onChange={(e) => setTimeElapsed(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter time elapsed"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Half-Life (T)</label>
                <input
                  type="number"
                  value={halfLife}
                  onChange={(e) => setHalfLife(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter half-life"
                />
              </div>
              <div>
                <button
                  onClick={calculateHalfLife}
                  className="bg-purple-500 hover:bg-purple-600 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                >
                  Calculate Decay
                </button>
              </div>

              {error && (
                <div className="mt-4 p-4 bg-red-100 rounded">
                  <p className="text-red-800">{error}</p>
                </div>
              )}

              {result && (
                <div className="mt-4 p-4 bg-purple-100 rounded">
                  <div className="flex items-center">
                    <FaCalculator className="text-2xl text-purple-600 mr-2" />
                    <div>
                      <h3 className="text-lg font-bold text-purple-800">Results</h3>
                      <div className="mt-2 space-y-2">
                        <p className="text-purple-800">Initial Amount: {result.initialAmount}</p>
                        <p className="text-purple-800">Current Amount: {result.currentAmount}</p>
                        <p className="text-purple-800">Time Elapsed: {result.timeElapsed} s</p>
                        <p className="text-purple-800">Half-Life: {result.halfLife} s</p>
                        <p className="text-purple-800">Decay Constant: {result.decayConstant} s⁻¹</p>
                        <p className="text-purple-800">Remaining Amount: {result.remainingAmount}</p>
                        <p className="text-purple-800">Percentage Remaining: {result.percentageRemaining}%</p>
                        <p className="text-purple-800">Time for Current Amount: {result.timeForCurrent} s</p>
                        <p className="text-purple-800">Number of Half-Lives: {result.nHalfLives}</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 p-4 bg-white rounded-lg shadow-md">
                    <div className="flex items-center mb-4">
                      <FaLightbulb className="text-purple-500 mr-3" />
                      <h3 className="text-lg font-semibold">Solution Explanation</h3>
                    </div>
                    <p className="text-gray-600">
                      Using the radioactive decay formula:
                      <br />
                      <br />
                      N = N₀ * e^(-λt)
                      <br />
                      <br />
                      Where:
                      <br />
                      N₀ = Initial amount ({result.initialAmount})
                      <br />
                      N = Current amount ({result.currentAmount})
                      <br />
                      λ = Decay constant ({result.decayConstant} s⁻¹)
                      <br />
                      t = Time elapsed ({result.timeElapsed} s)
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {showGraph && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold mb-4">Decay Curve</h2>
              <Chart
                options={generateGraphData().options}
                series={generateGraphData().series}
                type="line"
                height={400}
              />
              <p className="mt-4 text-gray-600 text-center">
                Shows the exponential decay of the substance over time
              </p>
            </div>
          )}
        </div>

        <div className="mt-12 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">How to Use</h2>
          <ol className="list-decimal list-inside space-y-2">
            <li>Enter the initial amount of the substance (N₀)</li>
            <li>Enter the current amount of the substance (N)</li>
            <li>Enter the time elapsed (t)</li>
            <li>Enter the half-life (T)</li>
            <li>Click "Calculate Decay" to get the results</li>
            <li>View the decay curve and all calculated values</li>
          </ol>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">Features & Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="text-xl font-semibold mb-2">Comprehensive Calculations</h3>
              <p>Calculates all relevant decay parameters</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Interactive Visualization</h3>
              <p>Shows the exponential decay curve</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Educational Tool</h3>
              <p>Ideal for students learning nuclear physics</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Instant Feedback</h3>
              <p>Real-time validation and error messages for incorrect inputs</p>
            </div>
          </div>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">FAQs</h2>
          <div className="space-y-6">
            <div className="border-l-4 border-purple-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What is Half-Life?</h3>
              <p className="text-gray-600">
                Half-life is the time required for a quantity to reduce to half its initial value. It's commonly used in nuclear physics and chemistry.
              </p>
            </div>

            <div className="border-l-4 border-purple-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What is the decay constant?</h3>
              <p className="text-gray-600">
                The decay constant (λ) is a measure of how quickly a substance decays. It's related to the half-life by the equation λ = ln(2) / T.
              </p>
            </div>

            <div className="border-l-4 border-purple-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What units should I use?</h3>
              <p className="text-gray-600">
                You can use any consistent units, but the calculator assumes seconds for time. Amounts can be in any unit (grams, atoms, etc.) as long as they're consistent.
              </p>
            </div>

            <div className="border-l-4 border-purple-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">Why is this calculator useful?</h3>
              <p className="text-gray-600">
                This calculator helps:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Students understand radioactive decay concepts</li>
                  <li>Scientists analyze nuclear reactions</li>
                  <li>Teachers demonstrate exponential decay</li>
                  <li>Anyone interested in nuclear physics</li>
                </ul>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HalfLifeCalculator;
